<template>
  <flow-part :id="$options.name" class="no-height ">
    <template #heading>
      <h2>Jouw energieverbruik</h2>
    </template>
    <template #content>

      <div class="input-group vertical">
        <div v-auto-animate class="input-group horizontal electricity-field">
          <TextInput id="electricity-input" v-model.number.lazy="comparisonStore.filters.electricity"
            label="Stroom (normaal)" input-mode="numeric" type="number" class="electricity-field"
            :show-valid="!!comparisonStore.filters.electricity" @keydown="numbersOnly" />

          <div v-if="comparisonStore.filters.doubleMeter">
            <FieldLabelWithTooltip label="Stroom (dal)" id="electricity-input">
              <template #tooltip>
                <Tooltip position="top-right">
                  <template #text>
                    Weet je niet exact hoeveel stroom je in de daluren verbruikt? Verdeel dan je totaalverbruik
                    gelijkmatig over normaal- en daluren.
                    <br> <br>
                    Je betaalt uiteindelijk alleen voor wat je daadwerkelijk verbruikt.
                  </template>
                </Tooltip>
              </template>
            </FieldLabelWithTooltip>
            <TextInput id="electricity-off-peak-input" v-model.number.lazy="comparisonStore.filters.electricityOffPeak"
              input-mode="numeric" type="number" class="electricity-field"
              :show-valid="!!comparisonStore.filters.electricityOffPeak" @keydown="numbersOnly" />
          </div>

        </div>
        <CheckboxInput id="smart-meter-input" v-model="comparisonStore.filters.doubleMeter" :value="true">
          Ik heb een slimme meter
        </CheckboxInput>
        <step-info-toggle title="Heb ik een slimme meter?" scroll-align="start">
          <template #content>
            <div id="smart-meter-info">
              <section>
                Alleen met een slimme meter of digitale meter wordt je normaal- en dalverbruik geregistreerd (sinds
                2021). Met een analoge meter wordt geen dalverbruik meer geregistreerd.
              </section>
              <section>
                <div class="img">
                  <img :src="smartMeterImage" alt="slimme meter">
                </div>
                <div class="text">
                  <p class="question">
                    Slimme (digitale) meter
                  </p>
                  <p>Een slimme meter of digitale meter herken je aan het digitale display.</p>
                </div>
              </section>
              <section>
                <div class="img">
                  <img :src="dumbMeterImage" alt="analoge meter">
                </div>
                <div class="text">
                  <p class="question">
                    Analoge meter
                  </p>
                  <p>Een meter met een ronddraaiende schijf en een telwerk.</p>
                </div>
              </section>
            </div>
          </template>
        </step-info-toggle>


        <div class="divider small"> </div>
        <div>
          <div v-auto-animate>
            <div v-if="comparisonStore.compareLocation && !comparisonStore.compareLocation.has_gas"
              class="card wizard-tip">
              <strong>Geen gasaansluiting</strong>
              <p>Op basis van je adres is er geen gasaansluiting. Als dit niet klopt, kun je hieronder je
                gasverbruik invoeren.</p>
            </div>
          </div>
          <CheckboxInput id="has-gas-input" v-model="comparisonStore.filters.noGas" :value="true" :true-value="false"
            :false-value="true">
            Ik heb gas
          </CheckboxInput>



          <div class="input-group gas-field" v-if="!comparisonStore.filters.noGas">
            <TextInput v-model.number.lazy="comparisonStore.filters.gas" label="Gas" inputmode="numeric"
              placeholder="Aantal" type="number" :class="gasFieldStatus" :show-valid="!!comparisonStore.filters.gas" />
          </div>
        </div>


        <div class="divider small"> </div>

        <CheckboxInput id="electricity-generated-input" v-model="comparisonStore.filters.electricityGenerated">
          Ik heb zonnepanelen
        </CheckboxInput>

        <section v-if="comparisonStore.filters.electricityGenerated">
          <div v-if="comparisonStore.filters.electricityGenerated" class="input-group solar-panels electricity-field">
            <TextInput v-model.number.lazy="comparisonStore.filters.electricityGeneration" label="Totale teruglevering"
              inputmode="numeric" placeholder="Aantal" type="number"
              :show-valid="!!comparisonStore.filters.electricityGeneration" />
          </div>

          <step-info-toggle title="Wat moet ik invullen?">
            <template #content>
              Vul de totale hoeveelheid stroom in die je teruglevert aan het net. Deze hoeveelheid kun je vinden op de
              jaarafrekening van je energieleverancier.
              <br><br>
              In de vergelijking rekenen we automatisch uit hoeveel je saldeert en welke terugleverkosten je moet
              betalen.
            </template>
          </step-info-toggle>
        </section>
      </div>
    </template>
  </flow-part>
</template>

<script>
import Icon from '../../../../vue-elements/icon/Icon.vue';
import StepInfoToggle from '../StepInfoToggle.vue';
import WizardStepMixin from '../../../../vue-elements/wizard/lib/WizardStepMixin';
import FlowPart from '../FlowPart.vue';
import { useEnergyComparisonStore } from '../../stores/energyComparison';
import TextInput from '../../../../vue-elements/input/TextInput.vue';
import CheckboxInput from '../../../../vue-elements/input/CheckboxInput.vue';
import FieldLabel from '../../../../vue-elements/field/FieldLabel.vue';
import FieldLabelWithTooltip from '../../../../vue-elements/field/FieldLabelWithTooltip.vue';
import Tooltip from '../../../../../abstracts/elements/tooltip/tooltip.vue';

export default {
  name: 'ManualInputStep',
  mixins: [WizardStepMixin()],
  components: {
    CheckboxInput,
    TextInput,
    FlowPart,
    Icon,
    StepInfoToggle,
    FieldLabel,
    FieldLabelWithTooltip,
    Tooltip,
  },
  setup() {
    const comparisonStore = useEnergyComparisonStore();

    return {
      comparisonStore,
    };
  },
  computed: {
    dumbMeterImage() {
      return '/assets/vectors/analog-meter.svg';
    },
    smartMeterImage() {
      return '/assets/vectors/smart-meter.svg';
    },
    valid() {
      return (this.comparisonStore.filters.electricity !== null && this.comparisonStore.filters.gas !== null)
        && (this.comparisonStore.filters.doubleMeter ? this.comparisonStore.filters.electricityOffPeak !== null : true)
        && (this.comparisonStore.filters.electricityGenerated ? this.comparisonStore.filters.electricityGeneration !== null : true);
    },
    energyLabel() {
      if (this.comparisonStore.filters.doubleMeter) {
        return 'Stroomverbruik kWh (normaal)';
      }
      return 'Stroomverbruik kWh';
    },
    electricityFieldStatus() {
      return this.getNumberFieldStatus(this.comparisonStore.filters.electricity);
    },
    electricityOffPeakFieldStatus() {
      return this.getNumberFieldStatus(this.comparisonStore.filters.electricityOffPeak);
    },
    gasFieldStatus() {
      return this.getNumberFieldStatus(this.comparisonStore.filters.gas);
    },
  },
  watch: {
    'comparisonStore.filters.doubleMeter': {
      handler(newVal) {
        if (!newVal) {
          this.comparisonStore.filters.electricity += this.comparisonStore.filters.electricityOffPeak;
          this.comparisonStore.filters.electricityOffPeak = 0;
        } else {
          this.comparisonStore.filters.electricityOffPeak = Math.floor(this.comparisonStore.filters.electricity / 2);
          this.comparisonStore.filters.electricity = Math.ceil(this.comparisonStore.filters.electricity / 2);
        }
      },
    },
  },
  mounted() {
    this.focusNextInputOnEnter();
  },
  methods: {
    numbersOnly(event) {
      if (!!event.key.match(/[\W]/g) && (event.target.value.length < 7) || !(event.target.value.length < 7) && !['Backspace', 'Enter', 'Control', 'ArrowLeft', 'ArrowRight', 'Meta', 'Delete'].includes(event.key)) {
        event.preventDefault();
      }
    },
    validate() {
      this.status = this.valid ? 'complete' : 'invalid';
    },
    focusNextInputOnEnter() {
      this.$el.querySelectorAll('input[type="number"]')
        .forEach((element, index, array) => {
          element.addEventListener('keydown', (event) => {
            if (event.keyCode === 13 && event.target.nodeName === 'INPUT') {
              event.preventDefault();

              let nextElementIndex = null;
              array.forEach((el, index) => {
                if (element === el) {
                  nextElementIndex = index + 1;
                }
              });

              ((nextElementIndex !== null && array.item(nextElementIndex)) ? array.item(nextElementIndex)
                .focus() : element.blur());
            }
          });
        });
    },
    getNumberFieldStatus(fieldValue) {
      if (fieldValue < 0) {
        return 'field-error';
      }
      if (fieldValue === 0 || fieldValue === '') {
        return 'field-empty';
      }
      return 'field-valid';
    },
  },
};
</script>

<style scoped lang="scss">
@use "../../style/inputs" as *;
@use '../../../../../assets/scss/_helpers/display';

// Manual scoping of styles
#smart-meter-info {
  @include display.flex-col-start();

  section {
    @include display.flex-row-between();

    align-items: flex-start;
    padding: 8px 0;

    &:first-of-type {
      padding-top: 0;
    }

    &:last-of-type {
      padding-bottom: 0;
    }
  }

  .img {
    min-width: 64px;
    width: 64px;

    img {
      width: 100%;
    }
  }

  .text {
    margin-left: 16px;
  }
}
</style>
